/* Norea: one verified free gift per eligible cart, using Shopify's native discount. */
(() => {
  'use strict';
  const marker = 'norea-flash-mask-v1';
  const owned = (item, config) => String(item.variant_id) === String(config.variantId) && item.properties?._norea_gift === marker;
  const active = config => config.enabled && Number.isFinite(Date.parse(config.startsAt)) && Number.isFinite(Date.parse(config.endsAt)) && Date.now() >= Date.parse(config.startsAt) && Date.now() < Date.parse(config.endsAt);
  async function reconcile(config, api) {
    let cart = await api.read();
    const before = JSON.stringify(cart.items);
    const eligible = active(config) && cart.items.some(item => config.productIds.includes(String(item.product_id)) && !owned(item, config) && item.quantity > 0);
    const manualMask = cart.items.some(item => String(item.variant_id) === String(config.variantId) && !owned(item, config));
    let gifts = cart.items.filter(item => owned(item, config));
    // A manually added mask already qualifies for the native discount. Do not duplicate it.
    if (!eligible || manualMask) {
      for (const gift of gifts) cart = await api.change(gift.key, 0);
    } else {
      for (const gift of gifts.slice(1)) cart = await api.change(gift.key, 0);
      gifts = cart.items.filter(item => owned(item, config));
      if (!gifts.length) {
        await api.add({ id: config.variantId, quantity: 1, properties: { _norea_gift: marker } });
        cart = await api.read();
      } else if (gifts[0].quantity !== 1) {
        cart = await api.change(gifts[0].key, 1);
      }
      // Shopify, not the browser, must confirm the free price. Roll back a paid gift.
      gifts = cart.items.filter(item => owned(item, config));
      if (!gifts.length || gifts.some(item => item.final_line_price !== 0)) {
        for (const gift of gifts) cart = await api.change(gift.key, 0);
        throw new Error('Le masque offert n’a pas pu être appliqué. Il n’a pas été ajouté à votre commande.');
      }
    }
    return { cart, changed: before !== JSON.stringify(cart.items) };
  }
  // Exposed for integration tests; no Shopify credentials or price manipulation.
  window.NoreaNativeGift = { reconcile, owned, active };
  const configNode = document.getElementById('norea-native-gift-config');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  if (!config.variantId || !config.productIds.length) return;
  const nativeFetch = window.fetch.bind(window);
  const root = config.root;
  let tail = Promise.resolve();
  function enqueue(work) {
    const task = tail.then(work, work);
    tail = task.catch(() => {});
    return task;
  }
  function message(text) {
    let node = document.getElementById('norea-gift-message');
    if (!node) {
      node = document.createElement('div');
      node.id = 'norea-gift-message';
      node.setAttribute('role', 'alert');
      node.style.cssText = 'position:fixed;bottom:20px;left:16px;right:16px;z-index:10000;padding:16px;background:#fff5e9;color:#422a16;border:1px solid #cba97b;border-radius:12px;box-shadow:0 4px 20px #0002';
      document.body.append(node);
    }
    node.textContent = text;
    node.hidden = !text;
  }
  async function request(path, body) {
    const response = await nativeFetch(root + path, body ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : { cache: 'no-store' });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || 'Le panier n’a pas pu être vérifié. Réessayez avant de commander.');
    return data;
  }
  const api = {
    read: () => request('cart.js'),
    add: item => request('cart/add.js', { items: [item] }),
    change: (id, quantity) => request('cart/change.js', { id, quantity })
  };
  async function sync() {
    try { const result = await reconcile(config, api); message(''); return result; }
    catch (error) { message(error.message); throw error; }
  }
  window.NoreaNativeGift.prepareCheckout = () => enqueue(sync);
  const propertiesKey = item => JSON.stringify(Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)));
  const latestLine = (item, cart) => cart.items.find(line =>
    String(line.variant_id) === String(item.variant_id || item.id) &&
    propertiesKey(line) === propertiesKey(item) &&
    String(line.selling_plan_allocation?.selling_plan?.id || '') === String(item.selling_plan_allocation?.selling_plan?.id || '')
  );
  window.fetch = function(input, options) {
    const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, location.href);
    const method = (options?.method || input?.method || 'GET').toUpperCase();
    if (url.origin !== location.origin || method !== 'POST' || !/\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname)) return nativeFetch(input, options);
    return enqueue(async () => {
      const response = await nativeFetch(input, options);
      if (!response.ok) return response;
      let data;
      try { data = await response.clone().json(); } catch { return response; }
      let result;
      try { result = await sync(); }
      catch { result = { cart: await api.read() }; }
      if (/\/cart\/add(\.js)?$/.test(url.pathname)) {
        if (data.items) data.items = data.items.map(item => ({ ...item, ...latestLine(item, result.cart) }));
        else data = { ...data, ...latestLine(data, result.cart) };
      }
      const sections = Object.keys(data.sections || {}).slice(0, 5);
      if (sections.length) {
        const sectionUrl = new URL(location.pathname, location.origin);
        sectionUrl.searchParams.set('sections', sections.join(','));
        try {
          const rendered = await nativeFetch(sectionUrl).then(r => {
            if (!r.ok) throw new Error('Section request failed');
            return r.json();
          });
          for (const id of sections) if (typeof rendered?.[id] === 'string') data.sections[id] = rendered[id];
        } catch {
          message('Votre panier a été mis à jour. Ouvrez le panier pour voir le cadeau.');
        }
      }
      if (!/\/cart\/add(\.js)?$/.test(url.pathname)) data = { ...result.cart, sections: data.sections };
      const headers = new Headers(response.headers);
      headers.delete('content-length');
      headers.set('content-type', 'application/json');
      return new Response(JSON.stringify(data), { status: response.status, headers });
    });
  };
  let proceeding = false;
  async function checkout(event, proceed) {
    if (proceeding) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    try {
      await enqueue(sync);
      proceeding = true;
      proceed();
    } catch {
      // Require a retry; never proceed with an unverified automatically added mask.
    }
  }
  document.addEventListener('submit', event => {
    const form = event.target;
    if (event.submitter?.name === 'checkout' || /\/checkout/.test(form.action)) checkout(event, () => form.requestSubmit(event.submitter || undefined));
  }, true);
  document.addEventListener('click', event => {
    const buyButton = event.target.closest('[data-norea-buy-now]');
    if (buyButton) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (buyButton.disabled) return;
      const form = document.getElementById(buyButton.dataset.formId);
      const addButton = form?.querySelector('[name="add"]');
      if (!form || addButton?.disabled || addButton?.getAttribute('aria-disabled') === 'true') return;
      if (!form.reportValidity()) return;
      buyButton.disabled = true;
      buyButton.setAttribute('aria-busy', 'true');
      form.dataset.noreaBuyNow = 'true';
      form.requestSubmit(addButton);
      return;
    }
    const link = event.target.closest('a[href]');
    if (link && new URL(link.href).pathname.startsWith('/checkout')) checkout(event, () => location.assign(link.href));
  }, true);
  enqueue(sync).then(result => {
    // Reconcile carts carried across page loads, including offers that have ended.
    if (result.changed) location.reload();
  }).catch(() => {});
})();
